import { Link } from "react-router-dom";
import { Logo } from "src/components/Logo";
import { type Brand, useBrand } from "src/hooks/useBrand";
import { useThemeStore } from "src/stores/themeStore";
import { cn } from "src/utilities/shadcnUtils";

/**
 * Renders the active brand's logo. A white-label desk brand renders its
 * `logoUrl`; otherwise we fall back to the Fraction `<Logo/>`. Sized to match
 * `Logo` so it drops into the dashboard headers without layout shift.
 */
export const BrandLogo = ({
  brand,
  dark,
  className,
}: {
  brand: Brand;
  dark?: boolean;
  className?: string;
}) => {
  if (brand.Logo) {
    const props = {
      alt: brand.name,
      className: cn("object-contain object-left", brand.logoClassName, className),
      height: "100%",
      width: "100%",
    };
    if (dark && typeof brand.DarkLogo === "string") {
      return <img src={brand.DarkLogo} {...props} />;
    }
    if (typeof brand.Logo === "string") {
      return <img src={brand.Logo} {...props} />;
    }
    const LogoToUse = dark ? brand.DarkLogo || brand.Logo : brand.Logo;
    return <LogoToUse {...props} />;
  }
  return <Logo className={cn(brand.logoClassName, className)} />;
};

export const BrandedLogo = (props: { className?: string }) => {
  const theme = useThemeStore((s) => s.theme);
  const brand = useBrand();
  return <BrandLogo brand={brand} dark={theme === "dark"} {...props} />;
};

export const LinkedBrandedLogo = ({
  className,
  logoClassName,
  ...props
}: {
  className?: string;
  logoClassName?: string;
}) => {
  const brand = useBrand();
  return (
    <Link className={className} to="/" aria-label={`${brand.name} home`}>
      <BrandedLogo className={cn("text-foreground", logoClassName)} {...props} />
    </Link>
  );
};
